import { type AccessTokenMetadata } from '@urbanx/agx.support.client';
import {
  AgxBadge,
  AgxButton,
  AgxSpacer,
  AgxTypography,
  Icon_Refresh,
} from '@urbanx/agx.ui-components2';
import Panel from 'components/panel/Panel';

interface IntegrationCardProps {
  connected: boolean;
  externalUrl?: string;
  metadata?: AccessTokenMetadata;
  name: string;
  onReset: () => void;
  resetPending: boolean;
}

const getMetadataDetails = (metadata?: AccessTokenMetadata) =>
  metadata
    ? [
        ['User ID', metadata.userId],
        ['Name', metadata.name],
        ['Email', metadata.email],
        ['Account ID', metadata.accountId],
        ['Account name', metadata.accountName],
      ].filter((detail): detail is [string, string] => !!detail[1])
    : [];

const IntegrationCard = ({
  connected,
  externalUrl,
  metadata,
  name,
  onReset,
  resetPending,
}: IntegrationCardProps) => {
  const metadataDetails = getMetadataDetails(metadata);

  return (
    <Panel
      title={name}
      actionContent={
        <AgxBadge
          type="colourfill"
          size="sm"
          colour={connected ? 'green' : 'grey'}
          text={connected ? 'Connected' : 'Disconnected'}
        />
      }
      halfWidth
    >
      <AgxSpacer direction="vertical" gap="spacing-lg" fill>
        <AgxTypography id={`${name}-connection-summary`} type="body" size="small">
          {connected
            ? `This agent's ${name} account is connected.`
            : `This agent's ${name} account is not connected.`}
        </AgxTypography>

        {metadataDetails.length > 0 && (
          <AgxSpacer direction="vertical" gap="spacing-xs" fill>
            {metadataDetails.map(([fieldName, fieldValue]) => (
              <AgxTypography
                key={fieldName}
                id={`${name}-${fieldName}-metadata`}
                type="body"
                size="small"
              >
                {fieldName}: {fieldValue}
              </AgxTypography>
            ))}
          </AgxSpacer>
        )}

        <AgxSpacer direction="horizontal" gap="spacing-md" wrap>
          {externalUrl && (
            <AgxButton
              id={`open-${name}-integration`}
              text={`Open in ${name}`}
              variant="primary"
              size="small"
              renderLink={(props) => (
                <a href={externalUrl} target="_blank" rel="noopener noreferrer" {...props} />
              )}
            />
          )}

          <AgxButton
            id={`reset-${name}-integration`}
            text={resetPending ? 'Resetting' : 'Reset connection'}
            variant="hollow"
            size="small"
            danger
            leftIcon={<Icon_Refresh width={16} height={16} />}
            disabled={!connected || resetPending}
            onClick={onReset}
          />
        </AgxSpacer>
      </AgxSpacer>
    </Panel>
  );
};

export default IntegrationCard;
